import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys } from '@/constant/common'
import { IoTypeMask } from '@/types/controller'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

/**
 * SCRUM-1032 — every decoder of one type on one controller, in one array.
 *
 * The decoder grid pages a block at a time, which is right for a list the user
 * scrolls. The guided setup needs the opposite: the Station step has to build
 * its rows from the decoders the user marked as stations, and a controller's
 * decoders are the only thing it can build them from — after the guided reset
 * there is no station data yet. That needs the whole list in hand, not the
 * blocks that happen to have been scrolled past.
 *
 * Fetched once when the guided setup opens so the Station step does not start
 * with a spinner over a thousand rows.
 */

/** The endpoint's own ceiling is unknown; this keeps the round trips few. */
const PAGE_SIZE = 200
/** A controller that reports more than this is not one page short of a bug. */
const MAX_PAGES = 40

export interface DecoderSummary extends Record<string, any> {
  address: number
  index: number
  name?: string
  decoderType?: string
  twiId?: number
  twiCustomName?: string
}

/** Purposes that make a decoder a station candidate (SCRUM-1040). */
export const STATION_PURPOSES = ['Irrigation', 'Non-Irrigation']

/**
 * The purposes are free-text names from the server; compare them loosely so
 * "Non Irrigation" and "Non-Irrigation" are the same answer.
 */
const normalisePurpose = (purpose: unknown) =>
  String(purpose ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

const STATION_PURPOSE_KEYS = new Set(STATION_PURPOSES.map(normalisePurpose))

export const isStationDecoder = (decoder: DecoderSummary) =>
  STATION_PURPOSE_KEYS.has(normalisePurpose(decoder?.decoderType))

const numberOr = (value: unknown, fallback: number) => {
  // Number(null) and Number('') are 0, which would sort a missing address first.
  if (value === null || value === undefined || value === '') return fallback
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * SCRUM-1032 QC round 7, item 10 — decoders in address order, as numbers.
 *
 * The list is the decoder sequence on the wire, so that is the order it is
 * read in. Sorting by name moved a renamed decoder ("QC Stn A") to the far end
 * of the list the moment its save landed. The server is asked for address
 * order too, but it sorts by address only, so ties on index (and on the
 * sub-controller of a control hub) are settled here. A decoder with no address
 * goes last rather than first.
 */
export const compareDecodersByAddress = (
  a: DecoderSummary,
  b: DecoderSummary,
): number =>
  numberOr(a?.address, Number.MAX_SAFE_INTEGER) -
    numberOr(b?.address, Number.MAX_SAFE_INTEGER) ||
  numberOr(a?.index, 0) - numberOr(b?.index, 0) ||
  numberOr(a?.twiId, 0) - numberOr(b?.twiId, 0)

/**
 * Each decoder once. The pages are ordered by address alone, so decoders that
 * share an address (index 1 and 2, or the same address on two controllers of a
 * hub) can sit in any order between two requests, and one can come back on
 * both sides of a page boundary. Rows without an id are kept as they are.
 *
 * This only removes the repeats. The other half — a row skipped because its
 * twin took its place on the next page — needs a stable tie-break on the
 * server (a ThenBy on index/id), which is a backend change.
 */
export const dedupeDecodersById = (
  decoders: DecoderSummary[],
): DecoderSummary[] => {
  const seen = new Set<string>()
  return decoders.filter((decoder) => {
    const id = decoder?.id
    if (id === null || id === undefined || id === '') return true
    const key = String(id)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const fetchAllDecoders = async (
  interfaceId: number,
  type: number,
): Promise<DecoderSummary[]> => {
  const all: DecoderSummary[] = []

  for (let pageIndex = 1; pageIndex <= MAX_PAGES; pageIndex++) {
    const res: any = await controllerDataConfigApi.getAll({
      interfaceId,
      type,
      PageIndex: pageIndex,
      PageSize: PAGE_SIZE,
      // Without a SortBy the endpoint sorts by name. A stable server order
      // also keeps the 200-row pages from overlapping or skipping rows.
      SortBy: 'address',
      SortDirection: 'asc',
    } as any)

    const page: DecoderSummary[] = res?.data?.data ?? []
    all.push(...page)

    const totalCount: number | undefined = res?.data?.totalCount
    // Stop on a short page as well as on the count: a server that ignores
    // `PageSize` would otherwise have this loop asking for the same page until
    // it hit the cap.
    if (page.length < PAGE_SIZE) break
    if (typeof totalCount === 'number' && all.length >= totalCount) break
  }

  return dedupeDecodersById(all).sort(compareDecodersByAddress)
}

/** The cache key the whole-list query is held under. */
export const allDecodersQueryKey = (
  interfaceId: number,
  type: number = IoTypeMask.Output,
) => [queryKeys.guidedAllDecoders, interfaceId, type]

const useSearchAllDecoders = (
  interfaceId: number,
  type: number = IoTypeMask.Output,
  options?: { enabled?: boolean },
): UseQueryResult<DecoderSummary[], any> =>
  useQuery(
    allDecodersQueryKey(interfaceId, type),
    () => fetchAllDecoders(interfaceId, type),
    {
      enabled: (options?.enabled ?? true) && Number.isFinite(interfaceId),
      // Held for the sitting rather than refetched per save: the decoder saves
      // invalidate `queryKeys.controller`, and this list is a thousand rows.
      // The step that consumes it refetches when it opens.
      staleTime: Infinity,
      retry: 0,
    },
  )

export default useSearchAllDecoders
